import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../../context/AuthContext';
import {
  FitnessCenter as FitnessIcon,
  DirectionsRun as RunIcon,
  Restaurant as RestaurantIcon
} from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  border: 'none',
  marginBottom: theme.spacing(3),
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
}));

const goals = [
  { value: 'Perder peso', label: 'Perder peso', emoji: '📉', color: '#dbeafe', textColor: '#3b82f6' },
  { value: 'Ganar masa muscular', label: 'Ganar músculo', emoji: '💪', color: '#dcfce7', textColor: '#22c55e' },
  { value: 'Recomposición', label: 'Recomposición', emoji: '🔄', color: '#fce7f3', textColor: '#ec4899', description: 'Perder grasa + ganar músculo' },
  { value: 'Mantener peso', label: 'Mantener peso', emoji: '⚖️', color: '#fef3c7', textColor: '#f59e0b' },
  { value: 'Mejorar salud general', label: 'Salud general', emoji: '❤️', color: '#fee2e2', textColor: '#ef4444' },
];

const activityLevels = [
  { value: 'Sedentario', label: 'Sedentario', emoji: '🪑', description: 'Trabajo de oficina' },
  { value: 'Ligeramente activo', label: 'Ligero', emoji: '🚶', description: '1-3 días/semana' },
  { value: 'Moderadamente activo', label: 'Moderado', emoji: '🏃', description: '3-5 días/semana' },
  { value: 'Muy activo', label: 'Activo', emoji: '🏋️', description: '6-7 días/semana' },
  { value: 'Extremadamente activo', label: 'Atleta', emoji: '🥇', description: 'Entreno intenso' },
];

const GoalsPreferences = ({ userData, handleInputChange }) => {
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { authToken, refreshToken } = useAuth();

  useEffect(() => {
    fetchDietaryOptions();
  }, []);

  const fetchDietaryOptions = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/restricciones`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          await refreshToken();
          return fetchDietaryOptions();
        }
        throw new Error('Error al obtener las preferencias dietéticas');
      }

      const data = await response.json();
      setDietaryOptions(data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setError('No se pudieron cargar las preferencias dietéticas');
      setLoading(false);
    }
  };

  const handleDietaryPreferenceChange = (preferenceId) => {
    const newPreferences = userData.dietaryPreferences.includes(preferenceId)
      ? userData.dietaryPreferences.filter(id => id !== preferenceId)
      : [...userData.dietaryPreferences, preferenceId];
    handleInputChange('dietaryPreferences', newPreferences);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      {/* Goals Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#e0e7ff' }}>
              <FitnessIcon sx={{ color: '#667eea' }} />
            </IconBox>
            <Box>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                ¿Cuál es tu objetivo principal?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Esto nos ayudará a calcular tus macros
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {goals.map((goal) => (
              <Grid item xs={6} key={goal.value}>
                <Box
                  onClick={() => handleInputChange('goal', goal.value)}
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    border: '2px solid',
                    borderColor: userData.goal === goal.value ? '#667eea' : '#e2e8f0',
                    bgcolor: userData.goal === goal.value ? '#e0e7ff' : '#fff',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#667eea',
                      transform: 'scale(1.02)',
                    }
                  }}
                >
                  <Typography variant="h4" sx={{ mb: 1 }}>{goal.emoji}</Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={userData.goal === goal.value ? 700 : 500}
                    color={userData.goal === goal.value ? '#667eea' : '#64748b'}
                  >
                    {goal.label}
                  </Typography>
                  {goal.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {goal.description}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </StyledCard>

      {/* Activity Level Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#dbeafe' }}>
              <RunIcon sx={{ color: '#3b82f6' }} />
            </IconBox>
            <Box>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                Nivel de actividad física
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ¿Cuánto ejercicio realizas normalmente?
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {activityLevels.map((level) => (
              <Box
                key={level.value}
                onClick={() => handleInputChange('activityLevel', level.value)}
                sx={{
                  p: 2,
                  borderRadius: '16px',
                  border: '2px solid',
                  borderColor: userData.activityLevel === level.value ? '#667eea' : '#e2e8f0',
                  bgcolor: userData.activityLevel === level.value ? '#e0e7ff' : '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#667eea',
                  }
                }}
              >
                <Typography variant="h5" sx={{ mr: 2 }}>{level.emoji}</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={userData.activityLevel === level.value ? 700 : 500}
                    color={userData.activityLevel === level.value ? '#667eea' : '#1e293b'}
                  >
                    {level.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {level.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </StyledCard>

      {/* Dietary Preferences Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#dcfce7' }}>
              <RestaurantIcon sx={{ color: '#22c55e' }} />
            </IconBox>
            <Box>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                Preferencias dietéticas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Selecciona las que apliquen (opcional)
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {dietaryOptions.map((preference) => (
              <Chip
                key={preference._id}
                label={preference.name}
                onClick={() => handleDietaryPreferenceChange(preference._id)}
                sx={{
                  borderRadius: '12px',
                  py: 2.5,
                  px: 1,
                  fontSize: '0.9rem',
                  fontWeight: userData.dietaryPreferences.includes(preference._id) ? 600 : 400,
                  bgcolor: userData.dietaryPreferences.includes(preference._id) ? '#e0e7ff' : '#f8fafc',
                  color: userData.dietaryPreferences.includes(preference._id) ? '#667eea' : '#64748b',
                  border: '2px solid',
                  borderColor: userData.dietaryPreferences.includes(preference._id) ? '#667eea' : 'transparent',
                  '&:hover': {
                    bgcolor: userData.dietaryPreferences.includes(preference._id) ? '#c3dafe' : '#f1f5f9',
                  }
                }}
              />
            ))}
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default GoalsPreferences;